/**
 * Gestion du token d'authentification via cookie.
 *
 * - rememberMe = true  → cookie persistant 24h (survit à la fermeture du navigateur).
 * - rememberMe = false → cookie de session (effacé à la fermeture du navigateur).
 * - Compatible avec l'API existante : token.value.accessToken / token.value.refreshToken.
 */
import Cookies from 'universal-cookie';
import { ref, watch } from 'vue';

const cookies = new Cookies(null, { path: '/' });

const COOKIE_NAME = 'auth_token';

type TokenData = {
  accessToken: string | null;
  refreshToken: string | null;
  custommerId?: string | null;
};

/** Indique si l'utilisateur a coché "Me rappeler" */
export const rememberMe = ref(false);

/** Token d'authentification — réactif, synchronisé avec le cookie */
export const token = ref<TokenData | null>(
  cookies.get<TokenData>(COOKIE_NAME) ?? {
    accessToken: null,
    refreshToken: null,
  },
);

/** Persiste le token dans le cookie à chaque modification */
watch(
  token,
  (newVal) => {
    if (!newVal?.accessToken) {
      cookies.remove(COOKIE_NAME, { path: '/' });
      return;
    }

    const options = rememberMe.value
      ? {
          expires: new Date(Date.now() + 24 * 60 * 60 * 1000),
          path: '/',
          sameSite: 'strict' as const,
        }
      : {
          path: '/',
          sameSite: 'strict' as const,
        };

    cookies.set(COOKIE_NAME, newVal, options);
  },
  { deep: true },
);

/** Supprime le cookie et réinitialise le token */
export function clearToken() {
  cookies.remove(COOKIE_NAME, { path: '/' });
  token.value = { accessToken: null, refreshToken: null };
}
